import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCalendarAlt, FaUser, FaSearch, FaArrowRight } from 'react-icons/fa';
import {
  blogPosts,
  blogCategories,
  getPostsByCategory,
  searchPosts
} from '../data/blogData';
import { useApiData } from '../hooks/useApiData';
import { fetchBlogs, fetchBlogCategories, fetchImage } from '../repository/api';
import { formatExcerpt, formatBlogContent } from '../utils/textFormatter';
import SEOHead from '../components/SEOHead';

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  // Fetch blogs and categories from API
  const { data: blogsData, loading: blogsLoading, error: blogsError } = useApiData(() =>
    fetchBlogs({ per_page: 50, sort_by: 'created_at', sort_order: 'desc' })
  );
  const { data: categoriesData, loading: categoriesLoading, error: categoriesError } = useApiData(fetchBlogCategories);

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) console.log('BlogPage API Data received:', apiData);

    if (!apiData || !apiData.blogs || !Array.isArray(apiData.blogs) || apiData.blogs.length === 0) {
      if (isDebug) console.log('No valid blogs data found, using default data');
      return blogPosts;
    }

    return apiData.blogs.map((blog) => ({
      id: blog.id,
      title: blog.title || 'Untitled Blog Post',
      excerpt: formatExcerpt(blog.paragraph_explanation || 'No description available'),
      category: blog.category?.toLowerCase() || 'general',
      date: new Date(blog.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      author: blog.author_name || 'Anonymous',
      authorImage: null,
      authorBio: '',
      image: blog.image_path ? fetchImage(blog.image_path) : blogPosts[0].image,
      featured: false,
      tags: blog.tags || [],
      content: blog.sections ? formatBlogContent(blog.sections) : ''
    }));
  };

  // Transform categories data
  const transformCategoriesData = (apiData) => {
    if (!apiData || !apiData.categories || !Array.isArray(apiData.categories)) {
      return blogCategories;
    }

    return [
      { id: 'all', name: 'All Posts' },
      ...apiData.categories.map(category => ({
        id: category.toLowerCase().replace(/\s+/g, '-'),
        name: category
      }))
    ];
  };

  // Use API data or fallback to default
  const allBlogs = useMemo(() => {
    if (blogsData && !blogsLoading && !blogsError) {
      const transformed = transformApiData(blogsData);
      return transformed.length > 0 ? transformed : blogPosts;
    }
    return blogPosts;
  }, [blogsData, blogsLoading, blogsError]);

  const availableCategories = useMemo(() => {
    if (categoriesData && !categoriesLoading && !categoriesError) {
      return transformCategoriesData(categoriesData);
    }
    return blogCategories;
  }, [categoriesData, categoriesLoading, categoriesError]);

  // Filter posts based on category and search
  useEffect(() => {
    let filtered = allBlogs;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(post => post.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.category.toLowerCase().includes(query) ||
        post.author.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredPosts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [activeCategory, searchQuery, allBlogs]);

  // Debug logging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('BlogPage component state:', {
        blogsLoading,
        blogsError,
        categoriesLoading,
        categoriesError,
        hasApiData: !!blogsData,
        hasCategoriesData: !!categoriesData,
        allBlogsCount: allBlogs.length,
        filteredPostsCount: filteredPosts.length,
        availableCategoriesCount: availableCategories.length
      });
    }
  }, [blogsLoading, blogsError, categoriesLoading, categoriesError, blogsData, categoriesData, allBlogs.length, filteredPosts.length, availableCategories.length]);

  // Show error state if API call failed (but continue with fallback data)
  if (blogsError || categoriesError) {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) {
      console.error('Blog page API errors:', { blogsError, categoriesError });
      console.log('Using fallback blog data due to API errors');
    }
  }

  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <PageWrapper>
      <SEOHead
        title="Blog - ICT & Telecom Insights Kenya | Codewave Technologies Limited"
        description="Stay updated with the latest insights on bulk SMS marketing, USSD solutions, mobile payment trends, and digital communication strategies in Kenya. Expert articles on business messaging and telecom solutions."
        keywords="ICT blog Kenya, telecom solutions blog, bulk SMS tips, USSD development insights, mobile payment trends Kenya, business SMS marketing tips, enterprise messaging best practices, digital communication Kenya, customer engagement strategies, telecom API tutorials"
        url="https://www.codewave.co.ke/blog"
        canonical="https://www.codewave.co.ke/blog"
      />
      <PageHeader>
        <Container>
          <BreadcrumbNav>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbCurrent>Blog</BreadcrumbCurrent>
          </BreadcrumbNav>
          <PageTitle>Our Blog</PageTitle>
          <PageDescription>
            Insights, tips, and updates from our team of communication experts
          </PageDescription>
        </Container>
      </PageHeader>

      <Container>
        {/* Blog Filters */}
        <BlogFilters>
          <CategoryTabs>
            {availableCategories.map(category => (
              <CategoryTab
                key={category.id}
                isActive={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </CategoryTab>
            ))}
          </CategoryTabs>

          <SearchBox>
            <SearchInput
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchIcon><FaSearch /></SearchIcon>
          </SearchBox>
        </BlogFilters>

        {/* Blog Posts Grid */}
        {currentPosts.length > 0 ? (
          <>
            <BlogGrid>
              {currentPosts.map((post) => (
                <BlogPost key={post.id}>
                  <BlogPostImageWrapper>
                    <BlogPostImage src={post.image} alt={post.title} />
                    <BlogPostCategory>
                      {availableCategories.find(cat => cat.id === post.category)?.name || post.category}
                    </BlogPostCategory>
                  </BlogPostImageWrapper>

                  <BlogPostContent>
                    <BlogPostMeta>
                      <MetaItem>
                        <FaCalendarAlt />
                        <span>{post.date}</span>
                      </MetaItem>
                      <MetaItem>
                        <FaUser />
                        <span>{post.author}</span>
                      </MetaItem>
                    </BlogPostMeta>

                    <BlogPostTitle>{post.title}</BlogPostTitle>
                    <BlogPostExcerpt dangerouslySetInnerHTML={{ __html: post.excerpt }} />

                    <ReadMoreLink href={`/blog/${post.id}`}>
                      Read more <FaArrowRight />
                    </ReadMoreLink>
                  </BlogPostContent>
                </BlogPost>
              ))}
            </BlogGrid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination>
                <PaginationButton
                  onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </PaginationButton>

                <PaginationNumbers>
                  {[...Array(totalPages)].map((_, index) => (
                    <PaginationNumber
                      key={index}
                      isActive={currentPage === index + 1}
                      onClick={() => paginate(index + 1)}
                    >
                      {index + 1}
                    </PaginationNumber>
                  ))}
                </PaginationNumbers>

                <PaginationButton
                  onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </PaginationButton>
              </Pagination>
            )}
          </>
        ) : (
          <NoResultsMessage>
            No articles found matching your criteria. Please try a different search.
          </NoResultsMessage>
        )}
      </Container>
    </PageWrapper>
  );
};

export default BlogPage;

// === Animations ===
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// === Styled Components ===
const PageWrapper = styled.div`
  background-color: #f8f9fb;
  min-height: 100vh;
  margin-top: 10vh;
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, #105090, #2090B0);
  color: white;
  padding: 80px 0 60px;
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    padding: 60px 0 40px;
    margin-bottom: 40px;
  }
`;

const BreadcrumbNav = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  font-size: 14px;
`;

const BreadcrumbLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  
  &:hover {
    color: white;
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const BreadcrumbCurrent = styled.span`
  color: white;
  font-weight: 500;
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const PageDescription = styled.p`
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Blog Filters
const BlogFilters = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const CategoryTab = styled.button`
  padding: 8px 16px;
  background: ${props => props.isActive ? 'linear-gradient(90deg, #105090, #2090B0)' : 'white'};
  color: ${props => props.isActive ? 'white' : '#666'};
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.isActive ? '0 5px 15px rgba(0, 0, 0, 0.1)' : '0 3px 10px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const SearchBox = styled.div`
  position: relative;
  width: 250px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 30px;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2090B0;
    box-shadow: 0 0 0 3px rgba(32, 144, 176, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #2090B0;
  font-size: 14px;
`;

// Blog Grid
const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  width: 100%;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const BlogPost = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const BlogPostImageWrapper = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const BlogPostImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${BlogPost}:hover & {
    transform: scale(1.05);
  }
`;

const BlogPostCategory = styled.span`
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlogPostContent = styled.div`
  padding: 25px;
`;

const BlogPostMeta = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: #777;
  font-size: 14px;
  
  svg {
    margin-right: 6px;
    color: #2090B0;
  }
`;

const BlogPostTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  line-height: 1.4;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BlogPostExcerpt = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMoreLink = styled.a`
  display: flex;
  align-items: center;
  color: #2090B0;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 6px;
    font-size: 12px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: #105090;
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const NoResultsMessage = styled.div`
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  margin: 20px 0;
  color: #666;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
`;

// Pagination
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
  margin-bottom: 80px;
`;

const PaginationButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.disabled ? '#e0e0e0' : 'white'};
  color: ${props => props.disabled ? '#999' : '#105090'};
  border: 1px solid #e0e0e0;
  border-radius: 30px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.disabled ? '#e0e0e0' : '#f8f8f8'};
    box-shadow: ${props => props.disabled ? 'none' : '0 5px 15px rgba(0, 0, 0, 0.05)'};
  }
`;

const PaginationNumbers = styled.div`
  display: flex;
  margin: 0 20px;
`;

const PaginationNumber = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  margin: 0 5px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  background: ${props => props.isActive ? 'linear-gradient(90deg, #105090, #2090B0)' : 'white'};
  color: ${props => props.isActive ? 'white' : '#666'};
  box-shadow: ${props => props.isActive ? '0 5px 15px rgba(0, 0, 0, 0.1)' : '0 3px 10px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    transform: ${props => !props.isActive && 'translateY(-2px)'};
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;